/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { toast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useAppForm } from '@/components/form';
import { useCreateAndUpdateBewertungSchema } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/formSchema/useCreateAndUpdateBewertungSchema';
import z from 'zod';
import { useCreateBewertung } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useCreateBewertung/useCreateBewertung';

export function useForm({
  stationId,
  rotationsplanId,
}: {
  stationId: string;
  rotationsplanId: string;
}) {
  const [createBewertung] = useCreateBewertung();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const schema = useCreateAndUpdateBewertungSchema();

  async function handleSubmit({
    sterne,
    beurteilung,
  }: {
    sterne: number;
    beurteilung: string;
  }) {
    try {
      const result = await createBewertung({
        input: {
          stationId,
          sterne,
          beurteilung,
        },
      });

      const success =
        result.data.mitarbeiter_createRotationsplanBewertung.success;

      if (success) {
        navigate(`/mitarbeiter/rotationsplan/${rotationsplanId}`);
        toast({
          title: t(
            'domain.mitarbeiter.rotationsplan.rotationsplaene.createBewertung.success',
          ),
        });
      } else {
        toast({
          title: t(
            'domain.mitarbeiter.rotationsplan.rotationsplaene.createBewertung.error',
          ),
        });
      }
    } catch (error) {
      Sentry.captureException(error);
      toast({
        title: t(
          'domain.mitarbeiter.rotationsplan.rotationsplaene.createBewertung.error',
        ),
      });
    }
  }

  const form = useAppForm({
    defaultValues: (() => {
      return {
        sterne: 1,
        beurteilung: '',
      } satisfies z.infer<typeof schema> as z.infer<typeof schema>;
    })(),
    validators: {
      onChange: schema,
      onBlur: schema,
      onSubmit: schema,
    },
    onSubmit: async ({ value }) =>
      await handleSubmit({
        beurteilung: value.beurteilung,
        sterne: value.sterne,
      }),
  });

  return form;
}
